import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Typography,
} from "@mui/material";

export type RollsetInfo = {
	RollsetId: number | null;
	RollsetLetter: string | null;
	RollsetSectionName: string | null;
	RollsetStandName: string | null;
	RollsetTopRollName: string | null;
	RollsetBottomRollName: string | null;
	TopRollDiameter?: number | null;
	BottomRollDiameter?: number | null;
};

type Props = {
	show: boolean;
	entityName: string;
	entityType: "Roll" | "Donut" | "Arbor";
	actionDescription: string;
	rollsetInfo: RollsetInfo | null;
	onConfirm: () => void;
	onCancel: () => void;
};

const StatBox = ({
	label,
	value,
	centered = true,
}: {
	label: string;
	value: string | null;
	centered?: boolean;
}) => (
	<Box sx={{ textAlign: centered ? "center" : "left", flex: 1 }}>
		<Typography variant="subtitle1" fontWeight={600}>
			{value || "N/A"}
		</Typography>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
	</Box>
);

const RollCard = ({
	label,
	rollName,
	diameter,
}: {
	label: string;
	rollName: string | null;
	diameter?: number | null;
}) => (
	<Paper
		variant="outlined"
		sx={{
			flex: 1,
			p: 1.5,
			border: "none",
		}}
	>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="subtitle1" fontWeight={600}>
			{rollName || "N/A"}
		</Typography>
		{diameter != undefined && (
			<Typography variant="body2" color="text.secondary">
				{diameter?.toFixed(3) ?? "—"}
			</Typography>
		)}
	</Paper>
);

export const RollsetDeactivationConfirmModal = ({
	show,
	entityName,
	entityType,
	actionDescription,
	rollsetInfo,
	onConfirm,
	onCancel,
}: Props) => {
	if (!rollsetInfo) return null;

	return (
		<Dialog open={show} onClose={onCancel} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="center" gap={1}>
					<WarningAmberIcon color="warning" />
					<Typography variant="h6" component="span">
						Confirm Deactivation
					</Typography>
				</Stack>
			</DialogTitle>

			<IconButton
				size="small"
				onClick={onCancel}
				aria-label="close"
				sx={{ position: "absolute", top: 12, right: 12 }}
			>
				<CloseIcon fontSize="small" />
			</IconButton>

			<DialogContent dividers>
				<Stack spacing={2}>
					<Typography variant="body2" color="text.secondary">
						<strong>{entityName}</strong> ({entityType}) is currently in an
						active rollset:
					</Typography>

					<Paper
						variant="outlined"
						sx={{
							p: 2,
							bgcolor: "grey.50",
							borderColor: "success.main",
							borderWidth: 2,
							borderRadius: 2,
						}}
					>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							sx={{ mb: 2 }}
						>
							<Typography variant="subtitle2" fontWeight={600}>
								Rollset
							</Typography>
						</Stack>

						<Paper
							sx={{
								display: "flex",
								border: "none",
								p: 1.5,
								mb: 2,
								borderRadius: 1,
							}}
							elevation={0}
						>
							<StatBox label="Section" value={rollsetInfo.RollsetSectionName} />
							<StatBox label="Stand" value={rollsetInfo.RollsetStandName} />
							<StatBox label="Letter" value={rollsetInfo.RollsetLetter} />
						</Paper>

						<Stack direction="row" spacing={1.5}>
							<RollCard
								label="Top Roll"
								rollName={rollsetInfo.RollsetTopRollName}
								diameter={rollsetInfo.TopRollDiameter}
							/>
							<RollCard
								label="Bottom Roll"
								rollName={rollsetInfo.RollsetBottomRollName}
								diameter={rollsetInfo.BottomRollDiameter}
							/>
						</Stack>
					</Paper>

					<Typography variant="body2" color="text.secondary">
						{actionDescription} will automatically deactivate this rollset.
					</Typography>
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button variant="outlined" color="inherit" onClick={onCancel}>
					Cancel
				</Button>
				<Button variant="contained" color="warning" onClick={onConfirm}>
					Proceed
				</Button>
			</DialogActions>
		</Dialog>
	);
};
